import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import debounce from 'lodash.debounce'
import styled from 'styled-components'
import { rem } from 'polished'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import UserReviewTile from './UserReviewTile'
import useUserReviews from 'hooks/Reviews/useUserReviews'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import RatingDistribution from 'components/Common/RatingDistribution'
import RatingBreakdownItem from 'components/Common/RatingBreakdownItem'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import LineThumbsUpIcon from 'LuxKit/Icons/line/LineThumbsUpIcon'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import Group from '../../utils/Group'
import CSSBreakpoint from '../../utils/CSSBreakpoint'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import AssistChip from 'LuxKit/Chips/AssistChip'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import RadioInput from 'LuxKit/Radio/RadioInput'
import { ReviewCompanionshipType } from 'api/review'
import useToggle from 'hooks/useToggle'
import useAutoUpdatingRef from 'hooks/useAutoUpdatingRef'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import IconButton from 'LuxKit/Button/IconButton'
import {
  mapRatingDistribution,
  mapRatingBreakdown,
  getRecommendPercentageText,
} from 'lib/reviews/ratingBreakdownUtils'

type ReviewSortType = 'newest' | 'best_rating' | 'worst_rating' | 'recommend'

// Map rating range to filter value
const RANGE_TO_FILTER_MAP: Record<string, string> = {
  '9.0+': 'exceptional',
  '7.0-9.0': 'good',
  '5.0-7.0': 'okay',
  '3.0-5.0': 'poor',
  '1.0-3.0': 'very-poor',
}

// Reviews panel state type and reducer
interface ReviewsPanelState {
  loadedReviews: Array<App.Review>
  currentPage: number
  searchInput: string
  activeSearch: string
  sort: ReviewSortType
  ratingFilter: string
  travellerFilter: ReviewCompanionshipType | ''
}

type ReviewsPanelAction =
  | { type: 'SET_LOADED_REVIEWS'; payload: Array<App.Review> }
  | { type: 'SET_CURRENT_PAGE'; payload: number }
  | { type: 'INCREMENT_PAGE' }
  | { type: 'SET_SEARCH_INPUT'; payload: string }
  | { type: 'SET_SORT'; payload: ReviewSortType }
  | { type: 'SET_RATING_FILTER'; payload: string }
  | { type: 'SET_TRAVELLER_FILTER'; payload: ReviewCompanionshipType | '' }
  | { type: 'SUBMIT_SEARCH' }
  | { type: 'RESET_PAGINATION' }
  | { type: 'RESET'; payload?: ReviewSortType }

const DEFAULT_SORT: ReviewSortType = 'newest'

function getInitialState(initialSort?: ReviewSortType): ReviewsPanelState {
  return {
    loadedReviews: [],
    currentPage: 1,
    searchInput: '',
    activeSearch: '',
    sort: initialSort ?? DEFAULT_SORT,
    ratingFilter: '',
    travellerFilter: '',
  }
}

function reviewsPanelReducer(state: ReviewsPanelState, action: ReviewsPanelAction): ReviewsPanelState {
  switch (action.type) {
    case 'SET_LOADED_REVIEWS':
      return { ...state, loadedReviews: action.payload }
    case 'SET_CURRENT_PAGE':
      return { ...state, currentPage: action.payload }
    case 'INCREMENT_PAGE':
      return { ...state, currentPage: state.currentPage + 1 }
    case 'SET_SEARCH_INPUT':
      return { ...state, searchInput: action.payload }
    case 'SET_SORT':
      return { ...state, sort: action.payload }
    case 'SET_RATING_FILTER':
      return { ...state, ratingFilter: action.payload }
    case 'SET_TRAVELLER_FILTER':
      return { ...state, travellerFilter: action.payload }
    case 'SUBMIT_SEARCH':
      return { ...state, activeSearch: state.searchInput, currentPage: 1, loadedReviews: [] }
    case 'RESET_PAGINATION':
      return { ...state, currentPage: 1, loadedReviews: [] }
    case 'RESET':
      return getInitialState(action.payload)
    default:
      return state
  }
}

const PanelGrid = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;

  ${mediaQueryUp.desktop} {
    display: grid;
    grid-template-columns: ${rem(280)} 1fr;
    grid-template-rows: 1fr;
    column-gap: ${rem(24)};
    overflow-y: hidden;
  }
`

const Sidebar = styled.div`
  padding: ${rem(20)};

  ${mediaQueryUp.desktop} {
    border-bottom: none;
    overflow-y: auto;
  }
`

const RatingBreakdownSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  row-gap: ${rem(16)};
  column-gap: ${rem(24)};
  justify-items: center;
`

const RatingBreakdownSectionMobile = styled(Group)`
  padding: 0 ${rem(8)};
`

const ReviewsColumn = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 0;

  ${mediaQueryUp.desktop} {
    overflow-y: hidden;
  }
`

const FilterBar = styled.div`
  padding: ${rem(16)} ${rem(20)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.five};
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
`

const SearchInputWrapper = styled.div`
  position: relative;
  width: 100%;
`

const FilterChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)};
`

const RadioGroup = styled.div`
  padding: ${rem(16)};
`

const ReviewsList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 ${rem(8)} ${rem(20)};
  position: relative;
  flex: 1;
  min-height: 0;

  ${mediaQueryUp.desktop} {
    overflow-y: auto;
  }
`

const EmptyState = styled.div`
  padding: ${rem(40)} ${rem(20)};
  text-align: center;
`

const SearchResultInfo = styled.div`
  padding: ${rem(12)} ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.default.seven};
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: ${rem(24)} 0;
`

const PAGE_SIZE = 9

interface Props {
  isOpen: boolean
  onClose: () => void
  property?: App.OfferProperty | App.BedbankProperty
  isLPP?: boolean
  experience?: App.ExperienceOffer
  tour?: App.Tours.TourV2Offer
  initialRatingFilter?: string
  initialReviewId?: string
  initialSort?: ReviewSortType
}

function ReviewsPanel({
  isOpen,
  onClose,
  property,
  isLPP,
  experience,
  tour,
  initialRatingFilter = '',
  initialReviewId = '',
  initialSort,
}: Props) {
  const intl = useIntl()

  // Translated option arrays
  const sortOptions = useMemo(() => [
    { label: intl.formatMessage({ id: '5H709zFsqf', defaultMessage: 'Most recent', description: 'Sort option for most recent reviews' }), value: 'newest' as const },
    { label: intl.formatMessage({ id: 'Cjt79+UpQJ', defaultMessage: 'Highest rated', description: 'Sort option for highest rated reviews' }), value: 'best_rating' as const },
    { label: intl.formatMessage({ id: '3fAFSLnl9C', defaultMessage: 'Lowest rated', description: 'Sort option for lowest rated reviews' }), value: 'worst_rating' as const },
    { label: intl.formatMessage({ id: '0PiT/h5pDW', defaultMessage: 'Recommended', description: 'Sort option for recommended reviews' }), value: 'recommend' as const },
  ], [intl])

  const ratingOptions = useMemo(() => [
    { label: intl.formatMessage({ id: 'xaTbW590QY', defaultMessage: 'All', description: 'Rating filter option to show all reviews' }), value: '' },
    { label: intl.formatMessage({ id: '++sAkYw1oZ', defaultMessage: 'Exceptional (9-10)', description: 'Rating filter option for exceptional reviews' }), value: 'exceptional' },
    { label: intl.formatMessage({ id: 'beqg13EtBB', defaultMessage: 'Good (7-8)', description: 'Rating filter option for good reviews' }), value: 'good' },
    { label: intl.formatMessage({ id: 'LhNxSfj0iI', defaultMessage: 'Okay (5-6)', description: 'Rating filter option for okay reviews' }), value: 'okay' },
    { label: intl.formatMessage({ id: 'RpoPIOJ7/S', defaultMessage: 'Poor (3-4)', description: 'Rating filter option for poor reviews' }), value: 'poor' },
    { label: intl.formatMessage({ id: 'beMyakkwP0', defaultMessage: 'Terrible (1-2)', description: 'Rating filter option for terrible reviews' }), value: 'very-poor' },
  ], [intl])

  const travellerOptions = useMemo(() => [
    { label: intl.formatMessage({ id: 'ZnD75MEOKd', defaultMessage: 'All', description: 'Traveller filter option to show all reviews' }), value: '' as const },
    { label: intl.formatMessage({ id: 'gWe1xrqniT', defaultMessage: 'Partner', description: 'Traveller filter option for partner travel' }), value: 'partner' as const },
    { label: intl.formatMessage({ id: 'IiE8r/tqZE', defaultMessage: 'Family', description: 'Traveller filter option for family travel' }), value: 'family' as const },
    { label: intl.formatMessage({ id: 'fooL9JuFwK', defaultMessage: 'Friends', description: 'Traveller filter option for friends travel' }), value: 'friends' as const },
    { label: intl.formatMessage({ id: '3+a/SOrkS2', defaultMessage: 'Just myself', description: 'Traveller filter option for solo travel' }), value: 'alone' as const },
  ], [intl])

  // Track the trigger element - using state so we re-run effects when it mounts
  const [triggerElement, setTriggerElement] = useState<HTMLDivElement | null>(null)

  // Reviews panel state with useReducer - initialize with initialSort to avoid flash
  const [state, dispatch] = useReducer(reviewsPanelReducer, getInitialState(initialSort))
  const {
    loadedReviews,
    currentPage,
    searchInput,
    activeSearch,
    sort,
    ratingFilter,
    travellerFilter,
  } = state

  // Dropdown toggle states
  const [isSortOpen, , openSort, closeSort] = useToggle()
  const [isRatingOpen, , openRating, closeRating] = useToggle()
  const [isTravellerOpen, , openTraveller, closeTraveller] = useToggle()
  const isDesktopUp = useScreenSizeUp('desktop')

  // Refs for dropdown anchors
  const sortRef = useRef<HTMLButtonElement>(null)
  const ratingRef = useRef<HTMLButtonElement>(null)
  const travellerRef = useRef<HTMLButtonElement>(null)

  // Track which review ID we're currently loading pages for (prevents infinite loop)
  const loadingForReviewIdRef = useRef<string>('')

  const offset = (currentPage - 1) * PAGE_SIZE
  const [reviewsData, fetchingReviews] = useUserReviews({
    propertyProvider: isLPP ? 'bedbank' : 'luxuryescapes',
    propertyId: property?.id,
    experienceId: experience?.id,
    tourId: tour?.id,
    useExternalReviews: true,
    useHiddenForProperty: true,
    limit: PAGE_SIZE,
    offset,
    sort,
    ratings: ratingFilter ? [ratingFilter] : undefined,
    companionship: travellerFilter ? [travellerFilter] : undefined,
    search: activeSearch || undefined,
  })

  // Combine previously loaded reviews with current page
  const allReviews = useMemo(() => {
    return currentPage === 1
      ? (reviewsData?.reviews ?? [])
      : [...loadedReviews, ...(reviewsData?.reviews ?? [])]
  }, [currentPage, reviewsData?.reviews, loadedReviews])

  const hasMoreReviews = allReviews.length < (reviewsData?.total ?? 0)

  const onLoadMore = useCallback(() => {
    if (!fetchingReviews && hasMoreReviews) {
      dispatch({ type: 'SET_LOADED_REVIEWS', payload: allReviews })
      dispatch({ type: 'INCREMENT_PAGE' })
    }
  }, [allReviews, fetchingReviews, hasMoreReviews])

  // Keep latest onLoadMore in a ref so debounced function always calls current version
  const onLoadMoreRef = useAutoUpdatingRef(onLoadMore)

  // Debounced version of onLoadMore - stable function that won't reset timer
  const debouncedLoadMore = useMemo(
    () => debounce(() => onLoadMoreRef.current(), 200),
    [onLoadMoreRef],
  )

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedLoadMore.cancel()
    }
  }, [debouncedLoadMore])

  // Reset state when panel closes
  useEffect(() => {
    if (!isOpen) {
      dispatch({ type: 'RESET', payload: initialSort })
    }
  }, [isOpen, initialSort])

  // Set initial rating filter when panel opens
  useEffect(() => {
    if (isOpen && initialRatingFilter) {
      dispatch({ type: 'SET_RATING_FILTER', payload: initialRatingFilter })
    }
  }, [isOpen, initialRatingFilter])

  // Scroll to specific review when panel opens with initialReviewId
  useEffect(() => {
    if (isOpen && initialReviewId && !fetchingReviews && allReviews.length > 0) {
      // Check if the review exists in loaded reviews
      const reviewExists = allReviews.some(r => r.id === initialReviewId)

      if (reviewExists) {
        // Reset the loading tracker
        loadingForReviewIdRef.current = ''

        setTimeout(() => {
          const reviewElement = document.querySelector(`[data-review-id="${initialReviewId}"]`)
          if (reviewElement) {
            reviewElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        }, 100)
      } else if (hasMoreReviews && loadingForReviewIdRef.current !== initialReviewId) {
        // Only start loading if we haven't already for this review ID
        loadingForReviewIdRef.current = initialReviewId
        onLoadMore()
      }
    }

    // Reset when panel closes
    if (!isOpen) {
      loadingForReviewIdRef.current = ''
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialReviewId, fetchingReviews, allReviews, hasMoreReviews])

  // Reset pagination when filters change
  useEffect(() => {
    dispatch({ type: 'RESET_PAGINATION' })
  }, [sort, ratingFilter, travellerFilter])

  // Infinite scroll using Intersection Observer
  // Using triggerElement (state) instead of a ref so effect re-runs when element mounts
  useEffect(() => {
    if (!triggerElement || !isOpen) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !fetchingReviews && hasMoreReviews) {
          debouncedLoadMore()
        }
      },
      { threshold: 0.1 },
    )

    observer.observe(triggerElement)

    return () => {
      observer.disconnect()
    }
  }, [triggerElement, isOpen, fetchingReviews, hasMoreReviews, debouncedLoadMore])

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  // Search handlers
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'SET_SEARCH_INPUT', payload: e.target.value })
  }, [])

  const handleSearchSubmit = useCallback(() => {
    dispatch({ type: 'SUBMIT_SEARCH' })
  }, [])

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearchSubmit()
    }
  }, [handleSearchSubmit])

  // Sort handler
  const handleSortChange = useCallback((value: ReviewSortType) => {
    dispatch({ type: 'SET_SORT', payload: value })
    closeSort()
  }, [closeSort])

  // Rating filter handler
  const handleRatingChange = useCallback((value: string) => {
    dispatch({ type: 'SET_RATING_FILTER', payload: value })
    closeRating()
  }, [closeRating])

  // Rating distribution click handler
  const handleRatingDistributionClick = useCallback((ratingRange: string) => {
    const filter = RANGE_TO_FILTER_MAP[ratingRange] || ''
    dispatch({ type: 'SET_RATING_FILTER', payload: filter })
  }, [])

  // Traveller filter handler
  const handleTravellerChange = useCallback((value: ReviewCompanionshipType | '') => {
    dispatch({ type: 'SET_TRAVELLER_FILTER', payload: value })
    closeTraveller()
  }, [closeTraveller])

  // Get current labels for chips
  const currentSortLabel = sortOptions.find(o => o.value === sort)?.label ?? sortOptions[0].label
  const currentRatingLabel = ratingOptions.find(o => o.value === ratingFilter)?.label
  const currentTravellerLabel = travellerOptions.find(o => o.value === travellerFilter)?.label

  const displayableRating = property?.rating ?? experience?.rating ?? tour?.rating
  const isGoogleReviews = displayableRating?.reviewsSource === 'google'
  const offerRatingDisplayable = isOfferRatingDisplayable(displayableRating)

  // Extract rating breakdown data from property, experience, or tour
  const ratingBreakdown =
    property?.rating?.reviewsRatingBreakdown ??
    experience?.rating?.reviewsRatingBreakdown ??
    tour?.rating?.reviewsRatingBreakdown

  // Map rating distribution from backend format to frontend format
  const ratingDistributions = useMemo(
    () => mapRatingDistribution(ratingBreakdown?.ratingDistribution),
    [ratingBreakdown?.ratingDistribution],
  )

  // Map rating breakdown items from backend format to frontend format
  const ratingBreakdowns = useMemo(
    () => mapRatingBreakdown(ratingBreakdown),
    [ratingBreakdown],
  )

  // Get recommend percentage text
  const entityType = property ? 'hotel' : tour ? 'tour' : 'experience'
  const recommendText = useMemo(
    () => getRecommendPercentageText(ratingBreakdown?.recommendPercentage, entityType),
    [ratingBreakdown?.recommendPercentage, entityType],
  )

  // Translated UI labels
  const reviewsTitle = intl.formatMessage({
    id: 'GPatyoMYQi',
    defaultMessage: 'Reviews',
    description: 'Title for the reviews panel modal',
  })

  const reviewsFromGoogleSubtitle = intl.formatMessage({
    id: 'xMtKhagm4x',
    defaultMessage: 'Reviews from Google',
    description: 'Subtitle indicating reviews are sourced from Google',
  })

  const searchReviewsPlaceholder = intl.formatMessage({
    id: 'kjBSut0WeZ',
    defaultMessage: 'Search reviews',
    description: 'Placeholder text for the review search input',
  })

  const searchAriaLabel = intl.formatMessage({
    id: 'yEolh69F9G',
    defaultMessage: 'Search',
    description: 'Aria-label for the review search button',
  })

  const sortChipLabel = intl.formatMessage(
    {
      id: '58z+2fc/hw',
      defaultMessage: 'Sort: {sortLabel}',
      description: 'Chip label showing the currently selected review sort option',
    },
    { sortLabel: currentSortLabel },
  )

  const ratingsLabel = intl.formatMessage({
    id: 'NSMZzsuxrm',
    defaultMessage: 'Ratings',
    description: 'Chip label for ratings filter when no specific rating is selected',
  })
  const ratingsWithFilterLabel = intl.formatMessage(
    {
      id: '2bKOZENJlp',
      defaultMessage: 'Ratings: {filterLabel}',
      description: 'Chip label for ratings filter showing the selected rating',
    },
    { filterLabel: currentRatingLabel },
  )
  const ratingsChipLabel = ratingFilter ? ratingsWithFilterLabel : ratingsLabel

  const travellersLabel = intl.formatMessage({
    id: 'WDFev96J3Z',
    defaultMessage: 'Travellers',
    description: 'Chip label for travellers filter when no specific type is selected',
  })
  const travellersWithFilterLabel = intl.formatMessage(
    {
      id: 'A6SkYyV8hd',
      defaultMessage: 'Travellers: {filterLabel}',
      description: 'Chip label for travellers filter showing the selected type',
    },
    { filterLabel: currentTravellerLabel },
  )
  const travellersChipLabel = travellerFilter ? travellersWithFilterLabel : travellersLabel

  const noReviewsWithFilters = intl.formatMessage({
    id: 'pcwghpRYxs',
    defaultMessage: 'No reviews found with the selected filters',
    description: 'Empty state message when no reviews match the selected filters',
  })

  return (
    <ModalBase
      isOpen={isOpen}
      onClose={handleClose}
      mode="drawer-starboard"
      data-reviews-panel="true"
      size={isDesktopUp ? 'XXL' : 'max'}
    >
      <ModalHeader title={reviewsTitle} onCloseButtonClick={handleClose} />
      <ModalBody>
        <PanelGrid>
          {/* Sidebar with rating summary */}
          <Sidebar>
            <VerticalSpacer gap={24}>
              {/* Number Rating */}
              {offerRatingDisplayable && (
                <NumberRating
                  variant="xxl"
                  rating={displayableRating.score}
                  total={displayableRating.reviewsTotal}
                  ratingSubtitle={isGoogleReviews ? reviewsFromGoogleSubtitle : undefined}
                />
              )}
              {!isGoogleReviews && (
                <>
                  <CSSBreakpoint min="desktop">
                    <VerticalSpacer gap={24}>
                      {/* Rating Distribution */}
                      {ratingDistributions.length > 0 && (
                        <>
                          <VerticalSpacer gap={0}>
                            {ratingDistributions.map(item => (
                              <RatingDistribution
                                key={item.id}
                                label={item.label}
                                range={item.range}
                                progress={item.progress}
                                count={item.count}
                                width={225}
                                isPanel
                                onClick={() => handleRatingDistributionClick(item.range)}
                              />
                            ))}
                          </VerticalSpacer>
                          <Divider kind="primary" direction="horizontal" />
                        </>
                      )}

                      {/* Rating Breakdown */}
                      {ratingBreakdowns.length > 0 && (
                        <>
                          <RatingBreakdownSection>
                            {ratingBreakdowns.map(item => (
                              <RatingBreakdownItem
                                key={item.id}
                                score={item.score}
                                label={item.label}
                              />
                            ))}
                          </RatingBreakdownSection>
                          <Divider kind="primary" direction="horizontal" />
                        </>
                      )}
                    </VerticalSpacer>
                  </CSSBreakpoint>
                  <CSSBreakpoint max="tablet">
                    {/* Rating Distribution */}
                    {(ratingDistributions.length > 0 || ratingBreakdowns.length > 0) && (
                      <VerticalSpacer gap={16}>
                        {/* Rating Distribution */}
                        {ratingDistributions.length > 0 && (
                          <Group direction="vertical" verticalAlign="space-between" gap={4}>
                            {ratingDistributions.map(item => (
                              <RatingDistribution
                                key={item.id}
                                label={item.label}
                                range={item.range}
                                progress={item.progress}
                                count={item.count}
                                onClick={() => handleRatingDistributionClick(item.range)}
                              />
                            ))}
                          </Group>
                        )}
                        {/* Rating Breakdown */}
                        {ratingBreakdowns.length > 0 && (
                          <RatingBreakdownSectionMobile direction="horizontal" horizontalAlign="space-between">
                            {ratingBreakdowns.map(item => (
                              <RatingBreakdownItem
                                key={item.id}
                                score={item.score}
                                label={item.label}
                              />
                            ))}
                          </RatingBreakdownSectionMobile>
                        )}
                      </VerticalSpacer>
                    )}
                  </CSSBreakpoint>

                  {/* Recommendation */}
                  {recommendText && (
                    <BodyTextBlock
                      weight="bold"
                      variant="medium"
                      startIcon={<LineThumbsUpIcon colour="highlight-secondary" />}
                    >
                      {recommendText}
                    </BodyTextBlock>
                  )}
                </>
              )}
            </VerticalSpacer>
          </Sidebar>

          {/* Reviews Column with Filter Bar and List */}
          <ReviewsColumn>
            {/* Filter Bar */}
            {!isGoogleReviews && (
              <FilterBar>
                {/* Search Input */}
                <SearchInputWrapper>
                  <TextInputOnly
                    placeholder={searchReviewsPlaceholder}
                    value={searchInput}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyDown}
                    noValidationSpacing
                    endIcon={
                      <IconButton
                        kind="tertiary"
                        variant="dark"
                        size="small"
                        onClick={handleSearchSubmit}
                        aria-label={searchAriaLabel}
                      >
                        <LineSearchIcon />
                      </IconButton>
                    }
                  />
                </SearchInputWrapper>

                {/* Filter Chips */}
                <FilterChips>
                  {/* Sort Dropdown */}
                  <AssistChip
                    ref={sortRef}
                    size="medium"
                    variant="filled"
                    onClick={openSort}
                  >
                    {sortChipLabel}
                  </AssistChip>
                  <DropdownList
                    size="S"
                    open={isSortOpen}
                    onClose={closeSort}
                    anchorRef={sortRef}
                    triggerRef={sortRef}
                  >
                    <RadioGroup>
                      <Group direction="vertical" gap={16}>
                        {sortOptions.map(option => (
                          <RadioInput
                            key={option.value}
                            checked={sort === option.value}
                            onChange={() => handleSortChange(option.value)}
                            name="sort"
                            value={option.value}
                          >
                            {option.label}
                          </RadioInput>
                        ))}
                      </Group>
                    </RadioGroup>
                  </DropdownList>

                  {/* Ratings Filter Dropdown */}
                  <AssistChip
                    ref={ratingRef}
                    size="medium"
                    variant="filled"
                    onClick={openRating}
                  >
                    {ratingsChipLabel}
                  </AssistChip>
                  <DropdownList
                    size="S"
                    open={isRatingOpen}
                    onClose={closeRating}
                    anchorRef={ratingRef}
                    triggerRef={ratingRef}
                  >
                    <RadioGroup>
                      <Group direction="vertical" gap={16}>
                        {ratingOptions.map(option => (
                          <RadioInput
                            key={option.value}
                            checked={ratingFilter === option.value}
                            onChange={() => handleRatingChange(option.value)}
                            name="rating"
                            value={option.value}
                          >
                            {option.label}
                          </RadioInput>
                        ))}
                      </Group>
                    </RadioGroup>
                  </DropdownList>

                  {/* Travellers Filter Dropdown */}
                  <AssistChip
                    ref={travellerRef}
                    size="medium"
                    variant="filled"
                    onClick={openTraveller}
                  >
                    {travellersChipLabel}
                  </AssistChip>
                  <DropdownList
                    size="S"
                    open={isTravellerOpen}
                    onClose={closeTraveller}
                    anchorRef={travellerRef}
                    triggerRef={travellerRef}
                  >
                    <RadioGroup>
                      <Group direction="vertical" gap={16}>
                        {travellerOptions.map(option => (
                          <RadioInput
                            key={option.value}
                            checked={travellerFilter === option.value}
                            onChange={() => handleTravellerChange(option.value as ReviewCompanionshipType | '')}
                            name="traveller"
                            value={option.value}
                          >
                            {option.label}
                          </RadioInput>
                        ))}
                      </Group>
                    </RadioGroup>
                  </DropdownList>
                </FilterChips>
              </FilterBar>
            )}
            {/* Search Result Info */}
            {activeSearch && allReviews.length > 0 && (
              <SearchResultInfo>
                <BodyText variant="medium">
                  <FormattedMessage
                    id="3RVZ4sOd6i"
                    defaultMessage="{count, plural, one {# review} other {# reviews}} mentioned ''<strong>{searchTerm}</strong>''"
                    description="Search results summary showing how many reviews mention the search term"
                    values={{
                      count: allReviews.length,
                      searchTerm: activeSearch,
                      strong: (chunks) => <strong>{chunks}</strong>,
                    }}
                  />
                </BodyText>
              </SearchResultInfo>
            )}

            {/* Reviews List */}
            <ReviewsList>
              <LoadingIndicator floating visible={fetchingReviews && currentPage === 1} />

              {/* Empty state for no search results */}
              {activeSearch && allReviews.length === 0 && !fetchingReviews && (
                <EmptyState>
                  <BodyText variant="medium">
                    <FormattedMessage
                      id="ne1mg6dh+F"
                      defaultMessage="There are no results for ''<strong>{searchTerm}</strong>''"
                      description="Empty state message when search returns no review results"
                      values={{
                        searchTerm: activeSearch,
                        strong: (chunks) => <strong>{chunks}</strong>,
                      }}
                    />
                  </BodyText>
                </EmptyState>
              )}

              {/* Empty state for no reviews with filters */}
              {!activeSearch && allReviews.length === 0 && !fetchingReviews && (
                <EmptyState>
                  <BodyText variant="medium">
                    {noReviewsWithFilters}
                  </BodyText>
                </EmptyState>
              )}

              {allReviews.map(review => (
                <div key={review.id} data-review-id={review.id}>
                  <UserReviewTile
                    userReview={{
                      ...review,
                      experienceId: experience?.id,
                      tourId: tour?.id,
                    }}
                    variant="panel"
                    showReviewFiltersAndUserDetails
                    defaultExpanded={initialReviewId === review.id}
                  />
                </div>
              ))}

              {/* Infinite scroll trigger */}
              <div ref={setTriggerElement}>
                {fetchingReviews && currentPage > 1 && (
                  <LoadingContainer>
                    <LoadingIndicator visible />
                  </LoadingContainer>
                )}
              </div>
            </ReviewsList>
          </ReviewsColumn>
        </PanelGrid>
      </ModalBody>
    </ModalBase>
  )
}

export default React.memo(ReviewsPanel)
